import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
// Use the SAME client as attachments (points at Render API)
import api from "../../../apiClient";
import { refreshValues, refreshMultiParcel, getSupportedCads } from "../cadApi";
import RefreshValuesModal from "../components/RefreshValuesModal";

// Build an absolute URL from a relative one using axios baseURL (or window origin)
function toAbs(urlish) {
  if (!urlish) return "";
  try {
    const u = new URL(urlish);
    return u.toString(); // already absolute
  } catch {
    const base = api?.defaults?.baseURL || window.location.origin;
    return new URL(urlish, base).toString();
  }
}

// Central helper: given images.*, decide what URL the browser should use
function resolveCoverUrl(images) {
  const imgs = images || {};
  const apiBase = (api?.defaults?.baseURL || "").replace(/\/+$/, "");

  // Prefer the signed URL the server built for us. /files/secure/by-key requires
  // a time-limited signature, and an <img> tag cannot send an Authorization
  // header, so the credential has to be in the URL. Building it here would
  // produce an unsigned link that 403s once FILES_REQUIRE_SIGNATURE is on.
  if (imgs.signed_url) return imgs.signed_url;

  // Legacy fallback: older responses have no signed_url. Unsigned, so it only
  // works while signatures are still optional.
  if (apiBase && imgs.web_path) {
    const key = String(imgs.web_path).replace(/^\//, ""); // matches S3/R2 key
    return `${apiBase}/files/secure/by-key?key=${encodeURIComponent(key)}`;
  }

  // Fallbacks for dev/legacy:
  if (imgs.subject) return imgs.subject;
  if (imgs.web_url) return imgs.web_url;
  if (imgs.web_path) return toAbs(imgs.web_path);

  return "";
}

export default function CoverSection({ data, onChange, onSave, packet, valueOptions = [] }) {
  // ---------- helpers ----------
  const cleanDigits = (s) => (s == null ? "" : String(s).replace(/[^\d-]/g, ""));
  const formatCurrency = (v) => {
    const d = cleanDigits(v);
    if (!d) return "";
    const n = parseInt(d, 10);
    return Number.isFinite(n) ? `$${n.toLocaleString()}` : "";
  };
  const formatComma = (v) => {
    const d = cleanDigits(v);
    if (!d) return "";
    const n = parseInt(d, 10);
    return Number.isFinite(n) ? n.toLocaleString() : "";
  };
  const num = (v) => {
    if (v === "" || v == null) return 0;
    const s = String(v).replace(/[$,%\s,]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };
  const pctText = (v) =>
    Number.isFinite(v) ? `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}%` : "—";

  // ---------- initial values ----------
  const initialPI = data?.property_info || {};
  const initialImages = data?.images || {};

  const [form, setForm] = useState({
    // IDs & names
    account: initialPI.account ?? initialPI.parcel ?? "",
    site_name: initialPI.site_name || "",

    // Address
    address: initialPI.address || "",
    city: initialPI.city || "",
    state: initialPI.state || "TX",
    zip: initialPI.zip || "",

    // Parties
    client_name: initialPI.client_name || initialPI.company || "",
    owner_name: initialPI.owner_name || initialPI.owner || "",

    // Assessor
    assessor: initialPI.assessor || "",

    // Property details
    property_type: initialPI.property_type || "",
    year_built: initialPI.year_built || "",
    unit_type: initialPI.unit_type || "units",
    units: formatComma(initialPI.units || initialPI.sqft || ""),
    land_sf: formatComma(initialPI.land_sf || initialPI.land_area_sf || ""),
    occupancy: initialPI.occupancy || "",

    // Codes
    client_site_code: initialPI.client_site_code || initialPI.company_code || "",
    cad_location_code: initialPI.cad_location_code || initialPI.location_code || "",

    // Values — mode: "total" (single field) or "breakdown" (land + building → total)
    value_mode: initialPI.value_mode || "total",
    py_land: formatCurrency(initialPI.py_land || ""),
    py_building: formatCurrency(initialPI.py_building || ""),
    previous_year_value: formatCurrency(
      initialPI.previous_year_value || initialPI.py_appraised_value || ""
    ),
    cy_land: formatCurrency(initialPI.cy_land || ""),
    cy_building: formatCurrency(initialPI.cy_building || ""),
    current_year_value: formatCurrency(
      initialPI.current_year_value || initialPI.appraised_value || ""
    ),
    opinion_of_value: formatCurrency(initialPI.opinion_of_value || ""),
    opinion_source: initialPI.opinion_source || "custom",

    // Consultant
    consultant_name: initialPI.consultant_name || "",
    consultant_phone: initialPI.consultant_phone || "",
    consultant_email: initialPI.consultant_email || "",

    // Notes
    notes: initialPI.notes || initialPI.internal_notes || "",
  });

  // Thumbnail shown in browser — now we centralize logic via resolveCoverUrl
  const [coverUrl, setCoverUrl] = useState(resolveCoverUrl(initialImages));

  // reflect external rehydration (but don't wipe)
  useEffect(() => {
    const p = data?.property_info || {};
    const imgs = data?.images || {};
    setForm((prev) => ({
      ...prev,
      account: p.account ?? p.parcel ?? prev.account,
      site_name: p.site_name ?? prev.site_name,
      address: p.address ?? prev.address,
      city: p.city ?? prev.city,
      state: p.state ?? prev.state,
      zip: p.zip ?? prev.zip,

      client_name: p.client_name ?? p.company ?? prev.client_name,
      owner_name: p.owner_name ?? p.owner ?? prev.owner_name,

      assessor: p.assessor ?? prev.assessor,
      property_type: p.property_type ?? prev.property_type,
      year_built: p.year_built ?? prev.year_built,
      unit_type: p.unit_type ?? prev.unit_type,
      units: formatComma(p.units ?? p.sqft ?? prev.units),
      land_sf: formatComma(p.land_sf ?? p.land_area_sf ?? prev.land_sf),
      occupancy: p.occupancy ?? prev.occupancy ?? "",
      client_site_code: p.client_site_code ?? p.company_code ?? prev.client_site_code,
      cad_location_code: p.cad_location_code ?? p.location_code ?? prev.cad_location_code,
      value_mode: p.value_mode ?? prev.value_mode ?? "total",
      py_land: formatCurrency(p.py_land ?? prev.py_land),
      py_building: formatCurrency(p.py_building ?? prev.py_building),
      previous_year_value: formatCurrency(
        p.previous_year_value ?? p.py_appraised_value ?? prev.previous_year_value
      ),
      cy_land: formatCurrency(p.cy_land ?? prev.cy_land),
      cy_building: formatCurrency(p.cy_building ?? prev.cy_building),
      current_year_value: formatCurrency(
        p.current_year_value ?? p.appraised_value ?? prev.current_year_value
      ),
      opinion_of_value: formatCurrency(p.opinion_of_value ?? prev.opinion_of_value),
      opinion_source: p.opinion_source ?? prev.opinion_source ?? "custom",
      consultant_name: p.consultant_name ?? prev.consultant_name,
      consultant_phone: p.consultant_phone ?? prev.consultant_phone,
      consultant_email: p.consultant_email ?? prev.consultant_email,
      notes: p.notes ?? p.internal_notes ?? prev.notes,
    }));
    setCoverUrl(resolveCoverUrl(imgs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data || {})]);

  // Auto-sum land + building → total when in breakdown mode
  useEffect(() => {
    if (form.value_mode !== "breakdown") return;
    const pyL = num(form.py_land), pyB = num(form.py_building);
    const cyL = num(form.cy_land), cyB = num(form.cy_building);
    const pyTotal = pyL + pyB;
    const cyTotal = cyL + cyB;
    const pyStr = pyTotal > 0 ? formatCurrency(String(pyTotal)) : "";
    const cyStr = cyTotal > 0 ? formatCurrency(String(cyTotal)) : "";
    setForm((f) => {
      if (f.previous_year_value === pyStr && f.current_year_value === cyStr) return f;
      return { ...f, previous_year_value: pyStr, current_year_value: cyStr };
    });
  }, [form.value_mode, form.py_land, form.py_building, form.cy_land, form.cy_building]);

  // derived YoY
  const yoy = useMemo(() => {
    const prev = num(form.previous_year_value);
    const curr = num(form.current_year_value);
    if (!prev) return null;
    return ((curr - prev) / prev) * 100;
  }, [form.previous_year_value, form.current_year_value]);

  const yoyBadge = useMemo(() => {
    if (yoy === null) return null;
    const up = yoy > 0;
    const cls =
      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border " +
      (up
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-emerald-50 text-emerald-700 border-emerald-200");
    const sign = up ? "+" : "";
    return <span className={cls}>{sign}{pctText(yoy)}</span>;
  }, [yoy]);

  const fullAddress = useMemo(() => {
    const parts = [form.address, form.city, form.state, form.zip].filter(Boolean);
    return parts.join(", ");
  }, [form.address, form.city, form.state, form.zip]);

  // ---------- push changes into the builder draft (debounced) ----------
  // This is the key to make the TOP Save button work for Cover changes.
  const debTimer = useRef(null);
  useEffect(() => {
    if (!onChange) return;
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => {
      const nextDraft = {
        // DO NOT touch images here (upload endpoint persists those)
        ...(data || {}),
        property_info: {
          ...(data?.property_info || {}),
          ...form,
          parcel: form.account,
          _calc: { yoy_pct: yoy },
        },
      };
      onChange(nextDraft); // marks Cover section dirty in PacketBuilder
    }, 250);
    return () => {
      if (debTimer.current) clearTimeout(debTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form), yoy]);

  // ---------- helpers to locate the latest Cover section ----------
  const fetchLatestCover = async (packetId) => {
    const res = await api.get(`/bapb/packets/${packetId}`);
    const sections = Array.isArray(res?.data?.sections) ? res.data.sections : [];
    let cover = null;
    for (const s of sections) {
      if ((s.type || "").toLowerCase() !== "cover") continue;
      if (!cover || (s.order_index ?? 0) >= (cover.order_index ?? 0)) cover = s;
    }
    return cover;
  };

  // ---------- upload flow (persists immediately) ----------
  const fileInputRef = useRef(null);
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !packet?.id) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      // Use POST (your API route is a POST upload)
      const r = await api.post(`/bapb/packets/${packet.id}/cover/photo`, fd);
      const imgs = {
        subject: r?.data?.subject,
        web_url: r?.data?.web_url,
        web_path: r?.data?.web_path,
      };
      const url = resolveCoverUrl(imgs);
      if (url) setCoverUrl(url);

      // Pull fresh section from the server so local draft matches DB (images updated server-side)
      const updated = await fetchLatestCover(packet.id);
      if (updated) {
        onChange?.(updated.data);
        onSave?.(updated.data);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ---------- opinion source picker ----------
  const onPickOpinionSource = (srcType) => {
    if (!srcType || srcType === "custom") {
      setForm((f) => ({ ...f, opinion_source: "custom" }));
      return;
    }
    const opt = valueOptions.find((o) => o.type === srcType);
    const raw = opt?.value ?? 0;
    const formatted = raw > 0 ? `$${Math.round(raw).toLocaleString()}` : "";
    setForm((f) => ({
      ...f,
      opinion_source: srcType,
      opinion_of_value: formatted || f.opinion_of_value,
    }));
  };

  // ---------- manual Save Cover (optional local button – helper kept for future use) ----------
  /* eslint-disable-next-line no-unused-vars */
  const saveCoverMeta = async () => {
    if (!packet?.id) return;
    try {
      const cover = await fetchLatestCover(packet.id);
      if (!cover) {
        alert("No Cover section on this packet.");
        return;
      }

      const current = cover.data || {};
      const next = {
        ...current,
        property_info: {
          ...(current.property_info || {}),
          ...form,
          parcel: form.account,
          _calc: { yoy_pct: yoy },
        },
      };

      // Use the new canonical MERGE endpoint; it returns the updated section
      const res = await api.patch(
        `/bapb/packets/${packet.id}/sections/${cover.id}/merge`,
        { data: next }
      );

      const updated = res?.data?.section;
      if (updated && updated.data) {
        onChange?.(updated.data);
        onSave?.(updated.data);
        const p = updated.data.property_info || {};
        const imgs = updated.data.images || {};
        setForm((prev) => ({
          ...prev,
          account: p.account ?? p.parcel ?? prev.account,
          site_name: p.site_name ?? prev.site_name,
          address: p.address ?? prev.address,
          city: p.city ?? prev.city,
          state: p.state ?? prev.state,
          zip: p.zip ?? prev.zip,
          client_name: p.client_name ?? p.company ?? prev.client_name,
          owner_name: p.owner_name ?? p.owner ?? prev.owner_name,
          assessor: p.assessor ?? prev.assessor,
          property_type: p.property_type ?? prev.property_type,
          year_built: p.year_built ?? prev.year_built,
          unit_type: p.unit_type ?? prev.unit_type,
          units: formatComma(p.units ?? p.sqft ?? prev.units),
          land_sf: formatComma(p.land_sf ?? p.land_area_sf ?? prev.land_sf),
          client_site_code: p.client_site_code ?? p.company_code ?? prev.client_site_code,
          cad_location_code: p.cad_location_code ?? p.location_code ?? prev.cad_location_code,
          value_mode: p.value_mode ?? prev.value_mode ?? "total",
          py_land: formatCurrency(p.py_land ?? prev.py_land),
          py_building: formatCurrency(p.py_building ?? prev.py_building),
          previous_year_value: formatCurrency(
            p.previous_year_value ?? p.py_appraised_value ?? prev.previous_year_value
          ),
          cy_land: formatCurrency(p.cy_land ?? prev.cy_land),
          cy_building: formatCurrency(p.cy_building ?? prev.cy_building),
          current_year_value: formatCurrency(
            p.current_year_value ?? p.appraised_value ?? prev.current_year_value
          ),
          opinion_of_value: formatCurrency(p.opinion_of_value ?? prev.opinion_of_value),
          opinion_source: p.opinion_source ?? prev.opinion_source ?? "custom",
          consultant_name: p.consultant_name ?? prev.consultant_name,
          consultant_phone: p.consultant_phone ?? prev.consultant_phone,
          consultant_email: p.consultant_email ?? prev.consultant_email,
          notes: p.notes ?? p.internal_notes ?? prev.notes,
        }));
        setCoverUrl(resolveCoverUrl(imgs));
      } else {
        const latest = await fetchLatestCover(packet.id);
        if (latest) {
          onChange?.(latest.data);
          onSave?.(latest.data);
        }
      }
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Save failed");
    }
  };

  // ---------- Refresh values from CAD ----------
  const [cadLoading, setCadLoading] = useState(false);
  const [cadError, setCadError] = useState("");
  const [supportedCads, setSupportedCads] = useState([]);
  const [showCadInfo, setShowCadInfo] = useState(false);

  useEffect(() => {
    let alive = true;
    getSupportedCads()
      .then((list) => {
        if (alive) setSupportedCads(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleCadRefresh = useCallback(async () => {
    const accountStr = form.account || packet?.account_number;
    const taxYear = packet?.tax_year;
    if (!accountStr || !taxYear) {
      setCadError("Need account and tax year to refresh.");
      return;
    }
    setCadLoading(true);
    setCadError("");
    try {
      // Fetch sequentially so the first call's CF bypass + cache benefits the second
      const cyRes = await refreshMultiParcel({ year: taxYear, accountStr, cover_meta: data });
      const pyRes = await refreshMultiParcel({ year: taxYear - 1, accountStr, cover_meta: data });

      const cyRow = cyRes?.merged;
      const pyRow = pyRes?.merged;

      const updates = {};

      const fmtVal = (v) => (v != null && Number.isFinite(v) && v > 0) ? formatCurrency(String(v)) : "";

      if (form.value_mode === "breakdown") {
        // Check if provider supports land/improvement breakdown
        const cyHasBreakdown = cyRow?.ok && cyRow.raw && (cyRow.raw.land != null || cyRow.raw.improvement != null);
        const pyHasBreakdown = pyRow?.ok && pyRow.raw && (pyRow.raw.land != null || pyRow.raw.improvement != null);
        const anyOk = cyRow?.ok || pyRow?.ok;

        if (anyOk && !cyHasBreakdown && !pyHasBreakdown) {
          // Provider only returns totals — prompt user to switch
          const assessor = (form.assessor || "").trim() || "This CAD";
          const switchToTotal = window.confirm(
            `${assessor} does not provide a Land / Improvement breakdown in their historical values via our refresh service.\n\n` +
            `Click OK to switch to "Total Only" mode and populate your values, or Cancel to skip.`
          );
          if (switchToTotal) {
            setForm((f) => ({ ...f, value_mode: "total" }));
            // Populate total values
            if (cyRow?.ok) {
              const val = cyRow.raw?.total_market ?? cyRow.market_value;
              if (val != null && val > 0) updates.current_year_value = formatCurrency(String(val));
            }
            if (pyRow?.ok) {
              const val = pyRow.raw?.total_market ?? pyRow.market_value;
              if (val != null && val > 0) updates.previous_year_value = formatCurrency(String(val));
            }
          }
        } else {
          // Provider supports breakdown — populate land + building
          if (cyRow?.ok && cyRow.raw) {
            updates.cy_land = fmtVal(cyRow.raw.land);
            updates.cy_building = fmtVal(cyRow.raw.improvement);
          }
          if (pyRow?.ok && pyRow.raw) {
            updates.py_land = fmtVal(pyRow.raw.land);
            updates.py_building = fmtVal(pyRow.raw.improvement);
          }
        }
      } else {
        // Total-only mode — use total_market (or market_value fallback)
        if (cyRow?.ok) {
          const val = cyRow.raw?.total_market ?? cyRow.market_value;
          if (val != null && val > 0) updates.current_year_value = formatCurrency(String(val));
        }
        if (pyRow?.ok) {
          const val = pyRow.raw?.total_market ?? pyRow.market_value;
          if (val != null && val > 0) updates.previous_year_value = formatCurrency(String(val));
        }
      }

      // Collect warnings from both years
      const allWarnings = [...(cyRes?.warnings || []), ...(pyRes?.warnings || [])];

      // Harris County note — values come from the initial appraisal roll, not live HCAD
      const isHarris = /harris|hcad/i.test(form.assessor || "");
      if (isHarris && (cyRow?.ok || pyRow?.ok)) {
        allWarnings.push("Harris County values are pulled from the initial appraisal roll, not live HCAD data.");
      }

      if (Object.keys(updates).length) {
        setForm((f) => ({ ...f, ...updates }));
        const warnings = [];
        if (!cyRow?.ok) warnings.push(`CY (${taxYear}): ${cyRow?.error || "no data"}`);
        if (!pyRow?.ok) warnings.push(`PY (${taxYear - 1}): ${pyRow?.error || "no data"}`);
        warnings.push(...allWarnings);
        if (warnings.length) setCadError(warnings.join(" | "));
      } else {
        const msgs = [];
        if (!cyRow?.ok) msgs.push(`CY (${taxYear}): ${cyRow?.error || "no data"}`);
        if (!pyRow?.ok) msgs.push(`PY (${taxYear - 1}): ${pyRow?.error || "no data"}`);
        msgs.push(...allWarnings);
        setCadError(msgs.join(" | ") || "No values found for this account/year.");
      }
    } catch (err) {
      setCadError(err?.message || "CAD refresh failed.");
    } finally {
      setCadLoading(false);
    }
  }, [form.account, form.value_mode, packet?.account_number, packet?.tax_year, data]);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
        <div>
          <span className="text-slate-500">Account:</span>{" "}
          <strong>{form.account || packet?.account_number || "—"}</strong>
        </div>
        <div className="mt-1">
          <span className="text-slate-500">Property:</span>{" "}
          <strong>{form.site_name || fullAddress || "—"}</strong>
        </div>
      </div>

      {/* Cover Photo */}
      <div className="rounded-xl border border-gray-200 p-3">
        <div className="mb-2 text-sm font-medium">Cover Photo</div>
        {coverUrl ? (
          <div className="mb-3">
            <img src={coverUrl} alt="Cover" className="max-h-48 rounded-lg border" />
            <div className="text-[11px] text-gray-500 mt-1 break-all">{coverUrl}</div>
          </div>
        ) : (
          <div className="mb-3 text-sm text-slate-500">No photo selected.</div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickFile}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="rounded-lg px-3 py-2 border border-gray-200 bg-white hover:border-gray-300"
          >
            Upload photo
          </button>
          <div className="text-xs text-slate-500">
            Uploading overwrites the current cover photo for everyone on this packet.
          </div>
        </div>
      </div>

      {/* Address & IDs */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field
          label="Account"
          value={form.account}
          onChange={(v) => setForm({ ...form, account: v })}
        />
        <Field
          label="Site / Asset Name"
          value={form.site_name}
          onChange={(v) => setForm({ ...form, site_name: v })}
        />
        <Field
          label="Address"
          value={form.address}
          onChange={(v) => setForm({ ...form, address: v })}
        />
        <div className="grid grid-cols-3 gap-2">
          <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
          <Field label="ZIP" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
        </div>
      </div>

      {/* Client, Owner & Assessor */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Client"
          value={form.client_name}
          onChange={(v) => setForm({ ...form, client_name: v })}
        />
        <Field
          label="Owner"
          value={form.owner_name}
          onChange={(v) => setForm({ ...form, owner_name: v })}
        />
        <Field
          label="Assessor"
          value={form.assessor}
          onChange={(v) => setForm({ ...form, assessor: v })}
        />
      </div>

      {/* Property details */}
      <div className="grid sm:grid-cols-6 gap-3">
        <Field
          label="Property Type"
          value={form.property_type}
          onChange={(v) => setForm({ ...form, property_type: v })}
        />
        <Field
          label="Year Built"
          value={form.year_built}
          onChange={(v) => setForm({ ...form, year_built: v })}
        />
        <div>
          <label className="block text-xs text-gray-500 mb-1">Unit Type</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            value={form.unit_type}
            onChange={(e) => setForm({ ...form, unit_type: e.target.value })}
          >
            <option value="units">Units</option>
            <option value="SF">SF</option>
            <option value="beds">Beds</option>
          </select>
        </div>
        <Field
          label="Units"
          value={form.units}
          onChange={(v) => setForm({ ...form, units: formatComma(v) })}
          inputMode="numeric"
        />
        <Field
          label="Land SF"
          value={form.land_sf}
          onChange={(v) => setForm({ ...form, land_sf: formatComma(v) })}
          inputMode="numeric"
        />
        <Field
          label="Occupancy %"
          value={form.occupancy}
          onChange={(v) => {
            const digits = v.replace(/[^\d]/g, "");
            const clamped = digits ? String(Math.min(parseInt(digits, 10), 100)) : "";
            setForm({ ...form, occupancy: clamped });
          }}
          inputMode="numeric"
          placeholder="e.g. 95"
        />
      </div>

      {/* Codes / IDs */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field
          label="Client Site Code"
          value={form.client_site_code}
          onChange={(v) => setForm({ ...form, client_site_code: v })}
        />
        <Field
          label="CAD Location Code"
          value={form.cad_location_code}
          onChange={(v) => setForm({ ...form, cad_location_code: v })}
        />
      </div>

      {/* Values — mode toggle + breakdown or total */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 font-medium">Value Entry:</span>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            <button
              type="button"
              className={`px-3 py-1.5 ${form.value_mode === "total" ? "bg-slate-700 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              onClick={() => setForm({ ...form, value_mode: "total" })}
            >
              Total Only
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 border-l border-gray-300 ${form.value_mode === "breakdown" ? "bg-slate-700 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              onClick={() => setForm({ ...form, value_mode: "breakdown" })}
            >
              Land + Building
            </button>
          </div>
          <div className="ml-auto inline-flex items-center gap-1">
            <button
              type="button"
              disabled={cadLoading}
              onClick={handleCadRefresh}
              className={`rounded-md px-3 py-1.5 text-sm border ${
                cadLoading
                  ? "border-slate-200 bg-slate-100 text-slate-400"
                  : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-400"
              }`}
            >
              {cadLoading ? "Refreshing\u2026" : "Refresh from CAD"}
            </button>
            <button
              type="button"
              onClick={() => setShowCadInfo(true)}
              className="h-7 w-7 rounded-full border border-emerald-400 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 flex items-center justify-center text-sm"
              title={`Pulls PY & CY values using Packet Year: ${packet?.tax_year || "?"}\nSupported CADs: ${(supportedCads || []).join(", ") || "None yet"}`}
              aria-label="Refresh Values info"
            >
              &#8505;
            </button>
          </div>
        </div>

        <RefreshValuesModal
          open={showCadInfo}
          onClose={() => setShowCadInfo(false)}
          supportedCads={supportedCads}
          intro={
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                <strong>Refresh from CAD</strong> pulls <strong>Land</strong>,{" "}
                <strong>Building (Improvement)</strong>, and{" "}
                <strong>Total Market Value</strong> directly from the CAD website
                for both the previous and current year.
              </p>
              <p>
                Values are fetched using the <strong>Packet Year</strong>.
                <br />
                Current Packet Year: <strong>{packet?.tax_year || "—"}</strong>
                <br />
                Previous Year: <strong>{packet?.tax_year ? packet.tax_year - 1 : "—"}</strong>
              </p>
              <p>
                In <strong>Land + Building</strong> mode, land and improvement
                values are populated separately and the total is auto-calculated.
                In <strong>Total Only</strong> mode, only the total market value
                is populated.
              </p>
              {/harris|hcad/i.test(form.assessor || "") && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="text-sm font-semibold text-amber-800 mb-1">
                    Harris County Note
                  </div>
                  <p className="text-sm text-amber-700">
                    Harris County values are pulled from the{" "}
                    <strong>initial appraisal roll</strong>, not live HCAD data.
                    These are the values as of the notice date and may differ
                    from the current HCAD website if values have been adjusted.
                  </p>
                </div>
              )}
            </div>
          }
        />

        {cadError && (
          <div className={`text-xs rounded-lg px-3 py-1.5 ${
            /initial appraisal roll/i.test(cadError) && !/error|fail|no data/i.test(cadError)
              ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
              : "text-red-600 bg-red-50 border border-red-200"
          }`}>
            {cadError}
          </div>
        )}

        {form.value_mode === "breakdown" ? (
          <>
            {/* Previous Year breakdown */}
            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <div className="text-xs font-medium text-gray-500">Previous Year</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field
                  label="Land"
                  value={form.py_land}
                  onChange={(v) => setForm({ ...form, py_land: formatCurrency(v) })}
                  inputMode="numeric"
                />
                <Field
                  label="Building"
                  value={form.py_building}
                  onChange={(v) => setForm({ ...form, py_building: formatCurrency(v) })}
                  inputMode="numeric"
                />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Total</label>
                  <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 text-sm font-medium">
                    {form.previous_year_value || <span className="text-slate-400">—</span>}
                  </div>
                </div>
              </div>
            </div>
            {/* Current Year breakdown */}
            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <div className="text-xs font-medium text-gray-500">Current Year</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field
                  label="Land"
                  value={form.cy_land}
                  onChange={(v) => setForm({ ...form, cy_land: formatCurrency(v) })}
                  inputMode="numeric"
                />
                <Field
                  label="Building"
                  value={form.cy_building}
                  onChange={(v) => setForm({ ...form, cy_building: formatCurrency(v) })}
                  inputMode="numeric"
                />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Total</label>
                  <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 text-sm font-medium">
                    {form.current_year_value || <span className="text-slate-400">—</span>}
                  </div>
                </div>
              </div>
            </div>
            {/* YoY */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-xs text-gray-500">YoY Change:</span>
              {yoyBadge || <span className="text-slate-500">—</span>}
            </div>
          </>
        ) : (
          /* Total-only mode (original layout) */
          <div className="grid sm:grid-cols-3 gap-3">
            <Field
              label="Previous Year Value"
              value={form.previous_year_value}
              onChange={(v) =>
                setForm({ ...form, previous_year_value: formatCurrency(v) })
              }
              inputMode="numeric"
            />
            <Field
              label="Current Year Value"
              value={form.current_year_value}
              onChange={(v) =>
                setForm({ ...form, current_year_value: formatCurrency(v) })
              }
              inputMode="numeric"
            />
            <div>
              <label className="block text-xs text-gray-500 mb-1">YoY Change</label>
              <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 text-sm">
                {yoyBadge || <span className="text-slate-500">—</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Opinion + Source */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Opinion of Value"
          value={form.opinion_of_value}
          onChange={(v) =>
            setForm({
              ...form,
              opinion_of_value: formatCurrency(v),
              opinion_source: "custom",
            })
          }
          inputMode="numeric"
        />
        <div>
          <label className="block text-xs text-gray-500 mb-1">Opinion Source</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            value={form.opinion_source || "custom"}
            onChange={(e) => onPickOpinionSource(e.target.value)}
          >
            <option value="custom">Custom</option>
            {valueOptions.map((opt) => {
              const label = opt.label;
              const hasVal = Number.isFinite(opt.value) && opt.value > 0;
              return (
                <option key={opt.type} value={opt.type} disabled={!hasVal}>
                  {label}
                  {hasVal ? ` — $${Math.round(opt.value).toLocaleString()}` : " — —"}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Consultant */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Consultant Name"
          value={form.consultant_name}
          onChange={(v) => setForm({ ...form, consultant_name: v })}
        />
        <Field
          label="Consultant Phone"
          value={form.consultant_phone}
          onChange={(v) => setForm({ ...form, consultant_phone: v })}
        />
        <Field
          label="Consultant Email"
          value={form.consultant_email}
          onChange={(v) => setForm({ ...form, consultant_email: v })}
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Notes</label>
        <textarea
          rows={4}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      {/* Actions (optional local Save) */}
      <div className="flex flex-wrap items-center gap-2">
        {fullAddress && (
          <a
            className="rounded-lg px-3 py-2 border border-gray-200 bg-white hover:border-gray-300 text-sm"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
            target="_blank"
            rel="noreferrer"
          >
            Open in Maps
          </a>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, inputMode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className="w-full rounded-lg border border-gray-300 px-3 py-2"
      />
    </div>
  );
}
